/** Knowledge graph type definitions — edge types, graph responses */

/** Fixed edge relationship types for knowledge graph */
export const EDGE_TYPES = [
  'relates-to',
  'depends-on',
  'extends',
  'references',
  'contradicts',
  'implements',
] as const
export type EdgeType = (typeof EDGE_TYPES)[number]

/** Inference status: 0=user-explicit, 1=ai-inferred, 2=user-confirmed */
export type InferredStatus = 0 | 1 | 2
export type GraphNodeKind = 'document' | 'entity'
export type GraphLinkSource = 'explicit' | 'semantic' | 'ai_relationship'
export type GraphExtractionProviderMode = 'cloudflare' | 'byok'
export type GraphExtractionJobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'skipped'
export type GraphExtractionTrigger = 'auto' | 'manual' | 'backfill'

/** Graph node (document) for Cytoscape.js visualization */
export interface GraphNode {
  id: string
  label: string
  kind?: GraphNodeKind
  category: string | null
  tags: string[]
  summary?: string | null
  degree?: number
  folderId?: string | null
  entityType?: string | null
  confidence?: number | null
}

/** Graph edge (explicit wikilink or implicit similarity) */
export interface GraphEdge {
  id: string
  source: string
  target: string
  type: EdgeType
  weight: number
  implicit: boolean
  linkSource?: GraphLinkSource
  context?: string | null
  score?: number
  confidence?: number | null
}

/** Full graph response for Cytoscape.js */
export interface GraphResponse {
  nodes: Array<{ data: GraphNode }>
  edges: Array<{ data: GraphEdge }>
  stats: {
    nodeCount: number
    edgeCount: number
    explicitEdges: number
    implicitEdges: number
  }
}

/** Graph traversal options */
export interface TraversalOptions {
  depth?: number
  types?: EdgeType[]
  direction?: 'outbound' | 'inbound' | 'both'
  includeImplicit?: boolean
  maxNodes?: number
}

/** Shortest path result */
export interface PathResult {
  path: Array<{ id: string; title: string }>
  edges: GraphEdge[]
  hops: number
}

/** Graph statistics */
export interface GraphStats {
  nodeCount: number
  edgeCount: number
  avgDegree: number
  density: number
  topConnected: Array<{ id: string; title: string; degree: number }>
  orphanCount: number
  edgeTypeDistribution: Record<string, number>
}

/** Similar document result */
export interface SimilarDoc {
  id: string
  title: string
  slug: string
  category?: string | null
  score: number
}

export interface GraphExtractionSettings {
  id: string
  tenantId: string
  enabled: boolean
  autoRunOnSave: boolean
  providerMode: GraphExtractionProviderMode
  cloudflareModel: string
  byokProviderId?: string | null
  byokModel?: string | null
  updatedAt: string
}

export interface GraphExtractionJob {
  id: string
  documentId: string | null
  trigger: GraphExtractionTrigger
  status: GraphExtractionJobStatus
  providerMode: GraphExtractionProviderMode
  providerId?: string | null
  model: string
  creditsCharged: number
  retryCount: number
  inputTokens: number
  outputTokens: number
  entityCount: number
  mentionCount: number
  docRelationshipCount: number
  entityRelationshipCount: number
  error?: string | null
  startedAt?: string | null
  finishedAt?: string | null
  createdAt: string
}

export interface DocumentGraphExtractionDetail {
  latestJob: GraphExtractionJob | null
  jobs: GraphExtractionJob[]
  relatedDocs: SimilarDoc[]
  entities: Array<{ id: string; name: string; type: string; confidence: number | null }>
}
